// src/components/ReadjudicacionMasiva/Auditoria/RegistroEditDialog.js

import React, { useState, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    IconButton,
    Box
} from '@mui/material';
import {
    Close as CloseIcon,
    Save as SaveIcon,
    RemoveCircle as RemoveCircleIcon,
    AddCircle as AddCircleIcon,
    Error as ErrorIcon
} from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        padding: theme.spacing(2)
    },
    dialogContent: {
        paddingTop: theme.spacing(3)
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2),
        color: theme.palette.primary.main
    },
    errorField: {
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: theme.palette.error.main,
                borderWidth: 2
            }
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.error.main,
            fontWeight: 600
        }
    },
    errorAlert: {
        marginBottom: theme.spacing(2),
        backgroundColor: theme.palette.error.light + '20',
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.error.main}`
    }
}));

const RegistroEditDialog = ({ open, registro, onClose, onSave, onExcluir, onReincluir }) => {
    const classes = useStyles();

    const [formData, setFormData] = useState({
        campo1: '',
        campo2: '',
        campo3: '',
        campo4: '',
        campo5: '',
        campo6: '',
        campo7: '',
        campo8: '',
        campo16: ''
    });

    // Determinar qué campo tiene error basado en la observación
    const getCampoConError = () => {
        if (!registro || !registro.Obs) return null;

        const obs = registro.Obs.toLowerCase();
        if (obs.includes('apellido') || obs.includes('nombre')) return 'campo1';
        if (obs.includes('cuit') || obs.includes('dni')) return 'campo2';
        if (obs.includes('certificado')) return 'campo3';
        if (obs.includes('monto')) return 'campo4';
        if (obs.includes('cuenta')) return 'campo6';
        if (obs.includes('domicilio')) return 'campo7';
        if (obs.includes('codigo postal') || obs.includes('código postal')) return 'campo8';

        return null;
    };

    const campoConError = getCampoConError();

    useEffect(() => {
        if (registro) {
            setFormData({
                campo1: registro.Campo1 || '',
                campo2: registro.Campo2 || '',
                campo3: registro.Campo3 || '',
                campo4: registro.Campo4 || '',
                campo5: registro.Campo5 || '',
                campo6: registro.Campo6 || '',
                campo7: registro.Campo7 || '',
                campo8: registro.Campo8 || '',
                campo16: registro.Campo16 || ''
            });
        }
    }, [registro]);

    const handleChange = (campo) => (event) => {
        setFormData({
            ...formData,
            [campo]: event.target.value
        });
    };

    const handleSave = () => {
        onSave(registro.Id, formData);
    };

    const handleExcluir = () => {
        if (onExcluir) {
            onExcluir(registro.Id);
        }
    };

    const handleReincluir = () => {
        if (onReincluir) {
            onReincluir(registro.Id);
        }
    };

    if (!registro) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle className={classes.dialogTitle}>
                <Typography variant="h6">
                    Editar Registro - Fila {registro.Fila}
                </Typography>
                <IconButton size="small" onClick={onClose} style={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                {/* Alerta de error si existe */}
                {registro.Ok === 0 && registro.Obs && (
                    <Box className={classes.errorAlert} display="flex" alignItems="center" gap={1}>
                        <ErrorIcon color="error" />
                        <Box>
                            <Typography variant="body2" style={{ fontWeight: 600, color: '#d32f2f' }}>
                                Error encontrado:
                            </Typography>
                            <Typography variant="body2">
                                {registro.Obs}
                            </Typography>
                        </Box>
                    </Box>
                )}

                <Typography variant="subtitle2" className={classes.sectionTitle}>
                    Datos del Demandado
                </Typography>

                <Grid container spacing={2}>
                    {/* Campo 1 - Nombre/Razón Social */}
                    <Grid size={12}>
                        <TextField
                            label="Nombre / Razón Social"
                            value={formData.campo1}
                            onChange={handleChange('campo1')}
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={campoConError === 'campo1' ? classes.errorField : ''}
                            error={campoConError === 'campo1'}
                        />
                    </Grid>

                    {/* Campo 2 - DNI/CUIT */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="DNI / CUIT"
                            value={formData.campo2}
                            onChange={handleChange('campo2')}
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={campoConError === 'campo2' ? classes.errorField : ''}
                            error={campoConError === 'campo2'}
                        />
                    </Grid>

                    {/* Campo 3 - Certificado */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Certificado"
                            value={formData.campo3}
                            onChange={handleChange('campo3')}
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={campoConError === 'campo3' ? classes.errorField : ''}
                            error={campoConError === 'campo3'}
                        />
                    </Grid>

                    {/* Campo 4 - Monto */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Monto"
                            type="number"
                            value={formData.campo4}
                            onChange={handleChange('campo4')}
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={campoConError === 'campo4' ? classes.errorField : ''}
                            error={campoConError === 'campo4'}
                        />
                    </Grid>

                    {/* Campo 5 - Impuesto */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Impuesto"
                            value={formData.campo5}
                            onChange={handleChange('campo5')}
                            variant="outlined"
                            fullWidth
                            size="small"
                        />
                    </Grid>

                    {/* Campo 6 - Cuenta */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Cuenta"
                            value={formData.campo6}
                            onChange={handleChange('campo6')}
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={campoConError === 'campo6' ? classes.errorField : ''}
                            error={campoConError === 'campo6'}
                        />
                    </Grid>

                    {/* Campo 7 - Domicilio */}
                    <Grid size={12}>
                        <TextField
                            label="Domicilio"
                            value={formData.campo7}
                            onChange={handleChange('campo7')}
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={campoConError === 'campo7' ? classes.errorField : ''}
                            error={campoConError === 'campo7'}
                        />
                    </Grid>

                    {/* Campo 8 - Código Postal */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Código Postal"
                            value={formData.campo8}
                            onChange={handleChange('campo8')}
                            variant="outlined"
                            fullWidth
                            size="small"
                            className={campoConError === 'campo8' ? classes.errorField : ''}
                            error={campoConError === 'campo8'}
                        />
                    </Grid>

                    {/* Campo 16 - Observaciones */}
                    <Grid size={12}>
                        <TextField
                            label="Observaciones"
                            value={formData.campo16}
                            onChange={handleChange('campo16')}
                            variant="outlined"
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions style={{ padding: 16, justifyContent: 'space-between' }}>
                <Box>
                    {registro.Ok === 3 ? (
                        <Button
                            onClick={handleReincluir}
                            color="primary"
                            variant="outlined"
                            startIcon={<AddCircleIcon />}
                        >
                            Reincluir Registro
                        </Button>
                    ) : (
                        <Button
                            onClick={handleExcluir}
                            color="secondary"
                            variant="outlined"
                            startIcon={<RemoveCircleIcon />}
                        >
                            Excluir Registro
                        </Button>
                    )}
                </Box>
                <Box>
                    <Button onClick={onClose} style={{ marginRight: 8 }}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        startIcon={<SaveIcon />}
                    >
                        Guardar
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default RegistroEditDialog;
